import React from 'react';
import { ExternalLink } from 'lucide-react';

import { Button } from '../ui/button';
import { formatDate } from '../../lib/formats';

/**
 * « Ce qui bloque », regroupé par camion.
 *
 * Une seule carte par camion, neutre (fond et bordure du thème). La gravité ne
 * passe que par une fine barre verticale et une pastille, à gauche de chaque
 * ligne: rouge pour un papier expiré ou une immobilisation, orange pour un
 * camion en atelier (ordre de travail ouvert). Le texte dit toujours l'état,
 * la couleur n'est jamais seule.
 */

const GRAVITE = {
  critique: { barre: 'bg-destructive', pastille: 'bg-destructive' },
  attention: { barre: 'bg-flame', pastille: 'bg-flame' },
};

/** « OT-2026-029EC6-0194 » devient « OT-0194 »: le fragment de société n'aide personne. */
export const numeroCourt = (numero) => {
  const texte = String(numero || '').trim();
  const m = texte.match(/^([A-Za-z]+)-\d{4}-[0-9A-Fa-f]{6}-(\d+)$/);
  return m ? `${m[1]}-${m[2]}` : texte;
};

/**
 * Décrit un blocage en trois morceaux lisibles: état, libellé court, et
 * l'infobulle (texte complet). Aucun préfixe technique.
 */
export const decrireBlocage = (item) => {
  const reason = String(item?.reason || '').trim();
  const code = item?.reason_code;

  if (code === 'document_expired') {
    const m = reason.match(/^Papier expiré\s*:\s*(.+?)(?:\s*\(([^()]*)\))?$/i);
    const nom = (m ? m[1] : reason.replace(/^Papier expiré\s*:\s*/i, '')).trim();
    const ref = m && m[2] && !/^sans référence$/i.test(m[2].trim()) ? m[2].trim() : '';
    return {
      etat: 'Expiré',
      gravite: 'critique',
      libelle: ref ? `${nom} · ${ref}` : nom || 'Papier expiré',
      titre: reason,
    };
  }

  if (code === 'work_order') {
    const m = reason.match(/^Ordre de travail\s+(\S+)\s+[—-]\s+(.*)$/);
    if (m) {
      const parties = m[2]
        .split(/\s+[—-]\s+/)
        .filter((p, i, tout) => !(i === 0 && tout.length > 1 && /^Entretien (préventif|correctif)$/i.test(p.trim())));
      return {
        etat: 'En atelier',
        gravite: 'attention',
        libelle: `${numeroCourt(m[1])} · ${parties.join(' — ') || 'Intervention'}`,
        titre: `${m[1]} — ${m[2]}`,
      };
    }
    return { etat: 'En atelier', gravite: 'attention', libelle: reason || 'Intervention en atelier', titre: reason };
  }

  const libelle =
    code === 'breakdown'
      ? reason.replace(/^Panne critique signalée\s*:\s*/i, 'Panne critique · ')
      : reason;
  return { etat: 'Immobilisé', gravite: 'critique', libelle: libelle || 'Immobilisé', titre: reason };
};

/** Regroupe par camion: le plus de blocages d'abord, puis le plus ancien. */
export const grouperParCamion = (items) => {
  const groupes = new Map();
  (items || []).forEach((item) => {
    const cle = item.truck_id || item.id;
    if (!groupes.has(cle)) groupes.set(cle, { truckId: item.truck_id, items: [] });
    groupes.get(cle).items.push(item);
  });
  const plusAncien = (g) =>
    g.items.reduce((min, i) => (i.started_at && (!min || i.started_at < min) ? i.started_at : min), '');
  const liste = [...groupes.values()];
  liste.forEach((g) => {
    g.items.sort((a, b) => String(a.started_at || '').localeCompare(String(b.started_at || '')));
  });
  return liste.sort(
    (a, b) =>
      b.items.length - a.items.length ||
      (plusAncien(a) || '9').localeCompare(plusAncien(b) || '9'),
  );
};

const TACTILE = 'min-h-10 sm:min-h-8';

export function BlocagesParCamion({ items, truckLabel, canWrite, onRelease, onOpenTruck }) {
  const groupes = grouperParCamion(items);
  return (
    // Un seul camion: sa carte occupe toute la largeur (pas de vide à droite).
    <div
      className={`grid grid-cols-1 items-start gap-3 ${groupes.length > 1 ? 'md:grid-cols-2' : ''}`}
      data-testid="blocages-par-camion"
    >
      {groupes.map((groupe) => {
        const plaque = truckLabel(groupe.truckId);
        const n = groupe.items.length;
        return (
          <section
            key={groupe.truckId || groupe.items[0].id}
            data-testid="blocage-camion"
            className="min-w-0 overflow-hidden rounded-xl border border-border bg-card"
          >
            <header className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-border/60 px-4 py-3">
              <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                <button
                  type="button"
                  onClick={() => onOpenTruck(groupe.truckId)}
                  className={`inline-flex ${TACTILE} min-w-0 items-center gap-1.5 rounded font-bold text-foreground underline-offset-4 hover:underline`}
                >
                  <span className="truncate">{plaque}</span>
                  <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                </button>
                <span className="text-xs text-muted-foreground">
                  {n} {n > 1 ? 'blocages' : 'blocage'}
                </span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className={TACTILE}
                onClick={() => onOpenTruck(groupe.truckId)}
              >
                Voir le camion
              </Button>
            </header>
            <ul className="divide-y divide-border/60">
              {groupe.items.map((item) => {
                const d = decrireBlocage(item);
                const g = GRAVITE[d.gravite];
                return (
                  <li
                    key={item.id}
                    data-testid="blocage-ligne"
                    data-gravite={d.gravite}
                    className="flex min-w-0 items-stretch gap-3 px-4 py-3"
                  >
                    <span
                      aria-hidden="true"
                      className={`w-1 shrink-0 self-stretch rounded-full ${g.barre}`}
                    />
                    <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-x-3 gap-y-2">
                      <div className="min-w-0 flex-1 basis-48 space-y-0.5">
                        <p
                          title={d.titre}
                          className="break-words text-sm font-medium text-foreground"
                        >
                          {d.libelle}
                        </p>
                        <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
                          <span
                            aria-hidden="true"
                            className={`inline-block h-2 w-2 rounded-full ${g.pastille}`}
                          />
                          <span>{d.etat}</span>
                          {item.started_at && (
                            <span>· depuis le {formatDate(item.started_at)}</span>
                          )}
                        </p>
                      </div>
                      {canWrite && (
                        <Button
                          size="sm"
                          variant="outline"
                          className={`${TACTILE} max-sm:w-full`}
                          onClick={() => onRelease(item)}
                        >
                          Lever
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

export default BlocagesParCamion;
